<template>
  <div>
    <riseup-header
      logo-color="white"
      color-variant="light-green"
    />

    <img
      class="bg-img"
      :src="topBg"
    >
    <div
      v-if="currentCoupon"
      class="green-bg"
    />
    <div
      class="new-subscription"
    >
      <div
        class="flex-column padding-horizontal-ml"
      >
        <div
          class="margin-top-xxl header-area "
          style="width: 88%"
        >
          <div class="flex-column ri-glbl-medium-display padding-top-l title">
            <span>Try RiseUp for free</span>
            <span> for 7 days </span>
          </div>
          <div class="margin-bottom-xl margin-top-xs ri-glbl-medium-body">
            Pick a plan to get started. <b> Cancel anytime.</b>
            Available only in the UK.

            <!-- Coupon Details Block -->
            <div
              v-if="currentCoupon"
            >
              <div class="coupon-block">
                <img :src="giftIcon">
                <p>Your <b>{{ formattedCoupon }}</b> coupon is already applied on these plans</p>
              </div>
            </div>
          </div>
        </div>

        <pricing-option
          class="margin-bottom-l first-plan"
          plan-text="Yearly"
          :plan-price-text="`£${plans[Plan.enum.Annual].price}`"
          :plan-price-period="plans[Plan.enum.Annual].period"
          :plan-price-subtext="`£${plans[Plan.enum.Annual].perMonth}/mo`"
          :is-selected="selectedPlan === Plan.enum.Annual"
          top-banner="Save 20%"
          top-banner-color="#205441"
          with-coach-session
          @click="updateSelectedPlan(Plan.enum.Annual)"
        />

        <pricing-option
          class="margin-bottom-l"
          plan-text="Quarterly"
          :plan-price-text="`£${plans[Plan.enum.Quarterly].price}`"
          :plan-price-period="plans[Plan.enum.Quarterly].period"
          :plan-price-subtext="`£${plans[Plan.enum.Quarterly].perMonth}/mo`"
          :is-selected="selectedPlan === Plan.enum.Quarterly"
          top-banner="Most Popular"
          top-banner-color="#00C070"
          with-coach-session
          @click="updateSelectedPlan(Plan.enum.Quarterly)"
        />

        <pricing-option
          class="margin-bottom-xxl"
          plan-text="Monthly"
          plan-price-text="£9.99"
          :plan-price-period="plans[Plan.enum.Monthly].period"
          :is-selected="selectedPlan === Plan.enum.Monthly"
          @click="updateSelectedPlan(Plan.enum.Monthly)"
        />

        <features />
      </div>
      <img
        class="bg-img"
        :src="bottomGraphic"
      >
      <button-footer
        class="footer"
        button-text="Start Your Free Trial"
        color="'beige'"
        :action="redirectToCheckout"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouteQuery } from '@vueuse/router';
import { useRoute } from 'vue-router';
import { Plan, PlanEnum } from '@riseupil/billing-client';
import bottomGraphic from '../../assets/bottom-graphic.svg';
import topBg from '../../assets/top-sub-bg.svg';
import giftIcon from '../../assets/gift.svg';
import RiseupHeader from '../Common/RiseupHeader.vue';
import ButtonFooter from '../Common/ButtonFooter.vue';
import PricingOption from './PricingOption.vue';
import Features from './Features.vue';
import { useSubscriptionStore } from '../../stores/subscription';
import Segment from '../../Segment';
import config from '../../config';
import { getCouponDetailsByPromoCode } from '../../api/subscriptionApi';

const route = useRoute();

const getQueryPlan = () => {
  const queryPlan = route.query.plan;
  if (queryPlan && Object.values(PlanEnum).includes(queryPlan as unknown as Plan)) {
    return queryPlan;
  }
  return null;
};

const selectedPlan = ref(getQueryPlan() || Plan.enum.Quarterly);
const currentCoupon = ref<any>(null);
const relevantPlans = ref<any[]>([]);

const plans = {
  [Plan.enum.Annual]: { price: 95.99, perMonth: 7.99, period: 'year', productId: config.get().annualProductId },
  [Plan.enum.Quarterly]: { price: 25.99, perMonth: 8.69, period: '3 months', productId: config.get().quarterlyProductId },
  [Plan.enum.Monthly]: { price: 9.99, perMonth: 9.99, period: 'month', productId: config.get().monthlyProductId },
};

const planTextMap = {
  [config.get().annualProductId]: 'Yearly',
  [config.get().quarterlyProductId]: 'Quarterly',
  [config.get().monthlyProductId]: 'Monthly',
};

const formattedCoupon = computed(() => {
  if (!currentCoupon.value) { return ''; }
  return formatCoupon(currentCoupon.value);
});

const subscriptionStore = useSubscriptionStore();

const getCouponDetails = async () => {
  const queryCoupon = route.query.coupon;
  if (queryCoupon) {
    const couponDetails: any = await getCouponDetailsByPromoCode(queryCoupon.toString());
    currentCoupon.value = couponDetails;
    console.log(couponDetails);
    relevantPlans.value = findRelevantPlans(couponDetails.appliesTo);
  }
};

const findRelevantPlans = (couponProducts: [string]) => {
  const relevantPlans = Object.values(plans).filter(plan => couponProducts.includes(plan.productId));
  return relevantPlans;
};

const formatCoupon = (coupon: any) => {
  let discountDescription = '';

  if (coupon.percentOff !== null && coupon.percentOff !== undefined) {
    discountDescription = `${coupon.percentOff}% off`;
  } else if (coupon.amountOff !== null && coupon.amountOff !== undefined) {
    discountDescription = `£${coupon.amountOff / 100} off`;
  } else {
    discountDescription = 'Discount not specified';
  }

  let durationDescription = '';

  if (coupon.duration === 'forever') {
    durationDescription = 'forever';
  } else if (coupon.duration === 'once') {
    durationDescription = 'once';
  } else if (coupon.duration === 'repeating') {
    if (coupon.durationInMonths !== null && coupon.durationInMonths !== undefined) {
      durationDescription = `for ${coupon.durationInMonths} months`;
    } else {
      durationDescription = 'for an unspecified number of months';
    }
  } else {
    durationDescription = 'for an unspecified duration';
  }

  return `${discountDescription} ${durationDescription}`;
};

selectedPlan.value = getQueryPlan() || Plan.enum.Quarterly;
getCouponDetails();

Segment.trackUserGot('SubscriptionPageEntered');

const fromApp = useRouteQuery<string>('fromApp');

const redirectToCheckout = async () => {
  Segment.trackUserInteraction('SubscriptionPageClicked', { selectedPlan: selectedPlan.value });
  const { coupon } = route.query;
  const checkoutUrl = await subscriptionStore.createCheckoutUrl(fromApp.value, plans[selectedPlan.value].productId, coupon);
  window.location.href = checkoutUrl;
};

const updateSelectedPlan = (plan: Plan) => {
  selectedPlan.value = plan;
  Segment.trackUserInteraction('SubscriptionPagePlanClicked', { selectedPlan: selectedPlan.value });
};

</script>

<style scoped lang="scss">
@import '@riseupil/base-ui/src/scss/riseup-colors';

.bg-img {
  width: 100%;
  display: block;
}

.green-bg {
  background-color: $riseup_green;
  height: 90px;
}

.new-subscription {
  background-color: #FCF7ED;

  .header-area {
    color: white;
    position: absolute;
    top: 20px;
  }

  .first-plan {
    margin-top: -13%;
    z-index: 3;
  }

  .plan-under-text {
    text-align: center;
  }

  .line {
    width: 100%;
    height: 1px;
    background-color: #20212526;
  }

  .footer {
    background-color: #FCF7ED;
  }

  .green-label {
    border-radius: 24px;
    background: $riseup_green;
    color: $riseup_light_green;
    width: 90%;
  }

  .coach-btn {
    width: 90%;
  }
}
.coupon-block {
  margin-top: 20px;
  display: flex; /* Use flexbox for layout */
  align-items: center; /* Center items vertically */
  background-color: #FFCF03;
  border-radius: 12px;
  padding: 10px; /* Adjust padding as needed */

}

.coupon-block img {
  width: 40px; /* Adjust width of the image */
  height: auto; /* Maintain aspect ratio */
  margin-right: 10px; /* Optional: Adjust spacing between image and text */
}

.coupon-block p {
  color: #205441;
  font-size: 14px;
  font-family: CircularStdBook;
  font-weight: 450;
  line-height: 24px;
  word-wrap: break-word;
}
</style>
